import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import SideBarContainer from 'Containers/SideBarContainer';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import ModalConfirm from './Components/ModalConfirm';
import { PermissionCard } from './Components/PermissionCard';
import { PermissionGroupCard } from './Components/PermissionGroupCard';
import { PermissionGroupDialog } from './Components/PermissionGroupDialog';
import { StatusSelect } from './Components/StatusSelect';
import Title from './Components/Title';
import { useRolesCreatePage } from './hooks/useRolesCreatePage';
import { Info, NameContainer, PageContainer, PermissionsGrid, StyledTextField } from './style';
import { groupPermissions, type PermissionGroup } from './utils/groupPermissions';

export default function RoleCreate() {
  // Obtenemos el ID del rol desde los parámetros de la URL
  const { id = null } = useParams<{ id: string }>();

  // Custom hook para manejar la lógica de creación/edición de roles
  const {
    register,
    open,
    openModal,
    closeModal,
    permissions,
    onChangeChecked,
    translation,
    t2,
    submit,
    watch,
    setValue,
  } = useRolesCreatePage({
    id,
  });

  const [activeGroupPrefix, setActiveGroupPrefix] = useState<string | null>(null);

  const groupedItems = useMemo(() => groupPermissions(permissions, t2), [permissions, t2]);

  const activeGroup: PermissionGroup | null = useMemo(() => {
    if (!activeGroupPrefix) return null;
    const found = groupedItems.find(item => item.kind === 'group' && item.group.prefix === activeGroupPrefix);
    return found?.kind === 'group' ? found.group : null;
  }, [activeGroupPrefix, groupedItems]);

  // Estado del formulario
  const nombre = watch('name');
  const permisos = watch('permissions');
  const canSubmit = nombre.length > 0 && permisos.length > 0;

  return (
    <SideBarContainer>
      <Title
        title={translation.title}
        subtitle={translation.subtitle}
        cancel={translation.cancel}
        save={translation.save}
        isDisabled={!canSubmit}
        eventButton={openModal}
      />
      <PageContainer>
        <div>
          <p>{translation.name}</p>
          <NameContainer>
            <StyledTextField {...register('name')} placeholder={translation.name} required />
            {permisos.length === 0 ? (
              <Info>
                <TipsAndUpdatesIcon style={{ color: '#00dcf0ff' }} />
                <span>{translation.default_alert}</span>
              </Info>
            ) : (
              <StatusSelect
                active={watch('active')}
                onChange={value => setValue('active', value === 'active')}
                placeholder={translation.status}
                activeText={translation.active}
                inactiveText={translation.inactive}
              />
            )}
          </NameContainer>
        </div>
        <PermissionsGrid>
          {groupedItems.map(item => {
            if (item.kind === 'single') {
              return (
                <PermissionCard
                  key={item.permission.id}
                  permission={item.permission}
                  onChangeChecked={onChangeChecked}
                />
              );
            }
            return (
              <PermissionGroupCard
                key={item.group.prefix}
                group={item.group}
                manageLabel={t2('group.manage')}
                activeCountLabel={(active, total) =>
                  t2('group.active_count').replace('{active}', String(active)).replace('{total}', String(total))
                }
                onClick={() => setActiveGroupPrefix(item.group.prefix)}
              />
            );
          })}
        </PermissionsGrid>
      </PageContainer>
      <ModalConfirm
        translation={translation}
        open={open}
        onAccept={() => {
          submit();
        }}
        onCancel={() => {
          closeModal();
        }}
      />
      <PermissionGroupDialog
        open={activeGroup !== null}
        group={activeGroup}
        onClose={() => setActiveGroupPrefix(null)}
        onChangeChecked={onChangeChecked}
      />
    </SideBarContainer>
  );
}
